import React, { Fragment } from "react";
import PropTypes from "prop-types";
import {
  useTable,
  useGlobalFilter,
  useAsyncDebounce,
  useSortBy,
  useFilters,
  useExpanded,
  usePagination,
} from "react-table";
import { Table, Button } from "reactstrap";
import { DefaultColumnFilter } from "./Filters";
import { BiSearchAlt } from "react-icons/bi";

function GlobalFilter({ globalFilter, setGlobalFilter }) {
  const [value, setValue] = React.useState(globalFilter);
  const onChange = useAsyncDebounce((value) => {
    setGlobalFilter(value || undefined);
  }, 200);

  return (
    <div>
      <div id="search-box">
        <div style={{ position: "relative", color: "#ADB5BD" }}>
          <label htmlFor="search-bar-0" id="search-bar-for">
            <input
              onChange={(e) => {
                setValue(e.target.value);
                onChange(e.target.value);
              }}
              id="search-bar-0"
              type="text"
              placeholder={`Pesquisa`}
              value={value || ""}
            />
          </label>
          <BiSearchAlt id="search-icon" />
        </div>
      </div>
    </div>
  );
}

const TableContainer = ({
  columns,
  data,
  isGlobalFilter,
  customPageSize,
  className,
  buttons,
  pagination,
  footer,
  linhaFooter,
  customMessage = "Nenhum registro encontrado",
}) => {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    state,
    preGlobalFilteredRows,
    setGlobalFilter,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data,
      defaultColumn: { Filter: DefaultColumnFilter },
      initialState: {
        pageIndex: 0,
        pageSize: customPageSize,
        sortBy: columns
          .filter((coluna) => coluna.isSorted)
          .map((coluna) => ({
            id: coluna.accessor,
            desc: coluna?.isSortedDesc ? true : false,
          })),
      },
      sortTypes: {
        alphanumeric: (row1, row2, columnName) => {
          const getValue = (value) => {
            if (value === undefined || value === null) {
              return null;
            }
            if (typeof value === "number") {
              return value;
            }
            if (typeof value === "string") {
              const numericValue = parseFloat(value);
              return isNaN(numericValue)
                ? value.toLowerCase().replace(/[^a-zA-Z0-9]/g, "")
                : numericValue;
            }
            return null;
          };

          const rowOneColumn = getValue(row1.values[columnName]);
          const rowTwoColumn = getValue(row2.values[columnName]);

          if (rowOneColumn === rowTwoColumn) {
            return 0;
          }

          const comparison = rowOneColumn > rowTwoColumn ? 1 : -1;
          return columnName.isSortedDesc ? comparison * -1 : comparison;
        },
      },
    },
    useGlobalFilter,
    useFilters,
    useSortBy,
    useExpanded,
    usePagination
  );

  const generateSortingIndicator = (column) => {
    if (!column.disableSortBy) {
      return column.isSorted ? (
        column.isSortedDesc ? (
          <span>
            <span style={{ opacity: 0.3 }}>↑</span>↓
          </span>
        ) : (
          <span>
            <span style={{ opacity: 0.3 }}>↓</span>↑
          </span>
        )
      ) : (
        <span style={{ opacity: 0.3 }}>↑↓</span>
      );
    } else {
      return null;
    }
  };

  const renderPaginationButtons = (isSmall) => {
    let MAX_BUTTONS = 6;
    const totalPages = pageOptions.length;

    const buttons = [];

    if (isSmall)
      return [
        <Button
          key={pageIndex}
          color={"primary"}
          onClick={() => gotoPage(pageIndex)}
          className="paginate_button page-item"
        >
          {pageIndex + 1}
        </Button>,
      ];
    if (totalPages <= MAX_BUTTONS) {
      for (let i = 0; i < totalPages; i++) {
        buttons.push(
          <Button
            key={i}
            color={i === pageIndex ? "primary" : "secondary"}
            onClick={() => gotoPage(i)}
            className="paginate_button page-item"
          >
            {i + 1}
          </Button>
        );
      }
    } else {
      if (pageIndex <= Math.floor(MAX_BUTTONS / 2)) {
        for (let i = 0; i < MAX_BUTTONS - 1; i++) {
          buttons.push(
            <Button
              key={i}
              color={i === pageIndex ? "primary" : "secondary"}
              onClick={() => gotoPage(i)}
              className="paginate_button page-item"
            >
              {i + 1}
            </Button>
          );
        }
        buttons.push(<span key="ellipsis">...</span>);
        buttons.push(
          <Button
            key={totalPages - 1}
            onClick={() => gotoPage(totalPages - 1)}
            className="paginate_button page-item"
          >
            {totalPages}
          </Button>
        );
      } else if (pageIndex >= totalPages - Math.floor(MAX_BUTTONS / 2) - 1) {
        buttons.push(
          <Button
            key={0}
            onClick={() => gotoPage(0)}
            className="paginate_button page-item"
          >
            1
          </Button>
        );
        buttons.push(<span key="ellipsis">...</span>);
        for (let i = totalPages - (MAX_BUTTONS - 1); i < totalPages; i++) {
          buttons.push(
            <Button
              key={i}
              color={i === pageIndex ? "primary" : "secondary"}
              onClick={() => gotoPage(i)}
              className="paginate_button page-item"
            >
              {i + 1}
            </Button>
          );
        }
      } else {
        buttons.push(
          <Button
            key={0}
            onClick={() => gotoPage(0)}
            className="paginate_button page-item"
          >
            1
          </Button>
        );
        buttons.push(<span key="ellipsis-start">...</span>);
        const startPageIndex = pageIndex - Math.floor(MAX_BUTTONS / 2) + 2;
        const endPageIndex = pageIndex + Math.floor(MAX_BUTTONS / 2) - 2;
        for (let i = startPageIndex; i <= endPageIndex; i++) {
          buttons.push(
            <Button
              key={i}
              color={i === pageIndex ? "primary" : "secondary"}
              onClick={() => gotoPage(i)}
              className="paginate_button page-item"
            >
              {i + 1}
            </Button>
          );
        }
        buttons.push(<span key="ellipsis-end">...</span>);
        buttons.push(
          <Button
            key={totalPages - 1}
            onClick={() => gotoPage(totalPages - 1)}
            className="paginate_button page-item"
          >
            {totalPages}
          </Button>
        );
      }
    }

    return buttons;
  };

  const [visibleColumns, setVisibleColumns] = React.useState(columns.length);
  const tableContainerRef = React.useRef(null);
  const reducedWidthsHistoryRef = React.useRef([]);
  const [expandedRows, setExpandedRows] = React.useState([]);
  const [isCollapsed, setIsCollapsed] = React.useState(false);

  const updateVisibleColumns = React.useCallback(() => {
    if (tableContainerRef.current) {
      const containerElement = tableContainerRef.current;
      const containerWidth = containerElement.getBoundingClientRect().width;
      const contentWidth = containerElement.scrollWidth;

      if (contentWidth > containerWidth) {
        setVisibleColumns((prevVisibleColumns) =>
          Math.max(1, prevVisibleColumns - 1)
        );

        if (!reducedWidthsHistoryRef.current.includes(containerWidth)) {
          reducedWidthsHistoryRef.current = [
            ...reducedWidthsHistoryRef.current,
            containerWidth,
          ];
        }
      } else if (
        (reducedWidthsHistoryRef.current &&
          reducedWidthsHistoryRef.current.length > 0 &&
          containerWidth >
            reducedWidthsHistoryRef.current[
              reducedWidthsHistoryRef.current.length - 1
            ]) ||
        (reducedWidthsHistoryRef.current.length === 0 &&
          columns.length > visibleColumns)
      ) {
        setVisibleColumns(Math.min(columns.length, visibleColumns + 1));

        reducedWidthsHistoryRef.current.pop();
      }

      if (columns.length === visibleColumns) {
        setExpandedRows(() => []);
        setIsCollapsed(() => false);
      } else {
        setIsCollapsed(() => true);
      }
    }
  }, [columns.length, visibleColumns]);

  React.useEffect(() => {
    updateVisibleColumns();
    window.addEventListener("resize", updateVisibleColumns);

    return () => {
      window.removeEventListener("resize", updateVisibleColumns);
    };
  }, [columns, updateVisibleColumns]);

  React.useEffect(() => {
    const dropdownMenus =
      tableContainerRef.current.querySelectorAll(".dropdown-menu");

    dropdownMenus.forEach(function (dropdownMenu) {
      if (isCollapsed) {
        dropdownMenu.classList.remove("dropdown-menu-end");
        dropdownMenu.classList.add("dropdown-menu-start");
      } else {
        dropdownMenu.classList.remove("dropdown-menu-start");
        dropdownMenu.classList.add("dropdown-menu-end");
      }
    });
  }, [isCollapsed, expandedRows]);

  const [isSmall, setIsSmall] = React.useState(false);

  React.useEffect(() => {
    const handleResize = () => {
      setIsSmall(window.innerWidth < 470);
    };
    window.addEventListener("resize", handleResize);
    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <Fragment>
      <div id="filtro-div">
        {isGlobalFilter && (
          <GlobalFilter
            preGlobalFilteredRows={preGlobalFilteredRows}
            globalFilter={state.globalFilter}
            setGlobalFilter={setGlobalFilter}
          />
        )}
        {buttons && (
          <div>
            <div className="dtr-buttons gap-2">{buttons}</div>
          </div>
        )}
      </div>

      <div ref={tableContainerRef}>
        <Table
          bordered
          hover
          {...getTableProps()}
          className={`${isCollapsed ? "collapsed" : ""} ${className}`}
        >
          <thead>
            {headerGroups.map((headerGroup) => (
              <tr key={headerGroup.id} {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.slice(0, visibleColumns).map((column) => (
                  <th
                    key={column.id}
                    style={column.largura ? { width: column.largura } : {}}
                  >
                    <div
                      {...column.getSortByToggleProps()}
                      className="datatable-header"
                    >
                      {column.render("Header")}
                      {generateSortingIndicator(column)}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody {...getTableBodyProps()}>
            {page.length > 0 ? (
              page.map((row, rowIndex) => {
                prepareRow(row);
                const isExpanded = expandedRows.includes(rowIndex);

                return (
                  <Fragment key={row.getRowProps().key}>
                    <tr>
                      {row.cells
                        .slice(0, visibleColumns)
                        .map((cell, columnIndex) => {
                          const { key, ...cellProps } = cell.getCellProps();
                          return (
                            <td key={key ?? columnIndex} {...cellProps}>
                              <div
                                style={
                                  columnIndex === 0 &&
                                  visibleColumns < columns.length
                                    ? { display: "flex", flexDirection: "row" }
                                    : {}
                                }
                              >
                                {columnIndex === 0 &&
                                visibleColumns < columns.length ? (
                                  <span
                                    className="plus-icon"
                                    onClick={() =>
                                      setExpandedRows((prevExpandedRows) =>
                                        isExpanded
                                          ? prevExpandedRows.filter(
                                              (row) => row !== rowIndex
                                            )
                                          : [...prevExpandedRows, rowIndex]
                                      )
                                    }
                                  >
                                    +
                                  </span>
                                ) : null}
                                {cell.render("Cell")}
                              </div>
                            </td>
                          );
                        })}
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td colSpan={row.cells.length}>
                          <ul className="dtr-details">
                            {row.cells
                              .slice(visibleColumns)
                              .map((cell, columnIndex) => (
                                <li
                                  key={cell.id}
                                  style={{
                                    padding: "0.5em 0",
                                    ...(columnIndex ===
                                    row.cells.slice(visibleColumns).length - 1
                                      ? {}
                                      : { borderBottom: "1px solid #efefef" }),
                                  }}
                                >
                                  {cell.render("Cell")}
                                </li>
                              ))}
                          </ul>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })
            ) : (
              <tr>
                <td id="text-sem-registros" colSpan={columns.length}>
                  {customMessage}
                </td>
              </tr>
            )}
          </tbody>

          {footer && linhaFooter && (
            <tfoot>
              <tr>
                {footer.slice(0, visibleColumns).map((column, index) => (
                  <td key={index}>
                    {column.accessor
                      ? column.accessor(linhaFooter)
                      : column.Cell
                        ? column.Cell({ row: { original: linhaFooter } })
                        : ""}
                  </td>
                ))}
              </tr>
            </tfoot>
          )}
        </Table>
      </div>

      {pagination ? (
        <div id="table-pagination-buttons">
          <div>
            {`Mostrando de
      ${pageIndex === 0 ? "" : pageIndex}1
      até
      ${
        pageIndex === pageOptions.length - 1
          ? preGlobalFilteredRows.length
          : pageSize * (pageIndex + 1)
      }
      de
      ${preGlobalFilteredRows.length} registros`}
          </div>
          <div>
            <div id="directional-buttons">
              <Button
                className="btn-rounded btn-directional"
                onClick={previousPage}
                disabled={!canPreviousPage}
              >
                {"<"}
              </Button>
              {renderPaginationButtons(isSmall)}
              <Button
                className="btn-rounded btn-directional"
                onClick={nextPage}
                disabled={!canNextPage}
              >
                {">"}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </Fragment>
  );
};

TableContainer.propTypes = {
  columns: PropTypes.array.isRequired,
  data: PropTypes.array.isRequired,
  isGlobalFilter: PropTypes.bool,
  customPageSize: PropTypes.number,
  className: PropTypes.string,
  buttons: PropTypes.node,
  pagination: PropTypes.bool,
  preGlobalFilteredRows: PropTypes.array,
  footer: PropTypes.array,
  linhaFooter: PropTypes.object,
};

GlobalFilter.propTypes = {
  globalFilter: PropTypes.string,
  setGlobalFilter: PropTypes.func.isRequired,
};

export default TableContainer;
